import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

/**
 * MayLogicCard
 *
 * A standalone card showing historical reduction statistics for a given
 * provider, plus a one-click recommended ask. Designed to live inline in
 * the new-negotiation form modal.
 *
 * Props:
 *   providerName      string  — the provider being negotiated
 *   billedAmount      number  — the lien/billed amount (for the suggested ask)
 *   currentAmount     string  — the value currently in the requested_amount input
 *                                (so we can highlight "you're already at MayLogic median")
 *   onApplyAmount     fn(amount: string) — called when user clicks "Use $X"
 *
 * Degrades gracefully: if the API errors or returns no data, the card
 * shows a quiet "No historical data" state instead of breaking the form.
 */
function MayLogicCard({ providerName, billedAmount, currentAmount, onApplyAmount }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!providerName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ name: providerName });
    if (billedAmount) params.set('billed_amount', String(billedAmount));
    axios
      .get(`${API_URL}/api/maylogic/provider-stats?${params.toString()}`, {
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;
        setStats(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 503) {
          setError('warehouse_unavailable');
        } else {
          setError('error');
        }
        console.warn('MayLogic fetch failed', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [providerName, billedAmount]);

  // Don't render anything while loading the first time — avoids the form
  // visually jumping when the card slides in.
  if (loading && !stats) return null;

  // Warehouse down or other API failure — render nothing
  if (error) return null;

  // No data at all for this provider name
  if (!stats || stats.n === 0) {
    return (
      <div style={styles.cardEmpty}>
        <div style={styles.eyebrowEmpty}>MAYLOGIC</div>
        <div style={styles.emptyMessage}>
          No historical data for <strong>{providerName}</strong>. Use your judgment.
        </div>
      </div>
    );
  }

  // n < 5 — too thin to recommend a number, but show the data we have
  if (stats.confidence === 'insufficient') {
    return (
      <div style={styles.cardEmpty}>
        <div style={styles.eyebrowEmpty}>MAYLOGIC</div>
        <div style={styles.emptyMessage}>
          Limited data — only {stats.n} past negotiation{stats.n === 1 ? '' : 's'} with{' '}
          <strong>{providerName}</strong>. Not enough to suggest an ask.
        </div>
      </div>
    );
  }

  const pct = stats.reduction_pct || {};
  const median = pct.p50;
  const p25 = pct.p25;
  const p75 = pct.p75;
  const suggestedPct = stats.suggested_reduction_pct;
  // Compute the dollar suggestion client-side from whichever amount we have:
  // billed amount (preferred) → otherwise use the value already in the form,
  // so the card stays useful even if the med record doesn't have lien_amount set.
  const baseAmount = billedAmount && billedAmount > 0
    ? billedAmount
    : (parseFloat(currentAmount) || 0);
  const suggested = baseAmount > 0 && suggestedPct != null
    ? Math.max(0, Math.round((baseAmount * (1.0 - suggestedPct)) * 100) / 100)
    : null;

  const confidenceLabel = {
    high: 'High Confidence',
    medium: 'Medium Confidence',
    low: 'Low Confidence',
  }[stats.confidence] || '';

  const confidenceColor = {
    high: '#16a34a',
    medium: '#ca8a04',
    low: '#dc2626',
  }[stats.confidence] || '#6b7585';

  const currentAmountNum = parseFloat(currentAmount) || 0;
  const matchesMedian =
    suggested != null && currentAmountNum > 0 && Math.abs(currentAmountNum - suggested) < 1;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>MAYLOGIC</div>
          <div style={styles.subtitle}>
            Based on {stats.n} past {providerName} negotiation{stats.n === 1 ? '' : 's'}
          </div>
        </div>
        <div style={{ ...styles.confidencePill, color: confidenceColor, borderColor: confidenceColor }}>
          {confidenceLabel}
        </div>
      </div>

      <div style={styles.body}>
        {suggestedPct != null && (
          <div style={styles.suggestion}>
            <div style={styles.suggestionLabel}>MayLogic Suggests</div>
            <div style={styles.suggestionAmount}>
              {suggested != null
                ? `$${suggested.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : `${(suggestedPct * 100).toFixed(0)}% reduction`}
            </div>
            <div style={styles.suggestionPct}>
              {(suggestedPct * 100).toFixed(0)}% reduction
              {baseAmount > 0 ? (
                <> off ${baseAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} billed</>
              ) : null}
            </div>
            <div style={styles.suggestionDetail}>
              Median accepted reduction is <strong>{(median * 100).toFixed(0)}%</strong>.
              Common range: {(p25 * 100).toFixed(0)}%–{(p75 * 100).toFixed(0)}%.
            </div>
            <div style={styles.ctaRow}>
              {matchesMedian ? (
                <div style={styles.matchedBadge}>
                  ✓ Your ask matches MayLogic median
                </div>
              ) : suggested != null ? (
                <button
                  type="button"
                  onClick={() => onApplyAmount && onApplyAmount(suggested.toFixed(2))}
                  style={styles.primaryBtn}
                >
                  Use ${suggested.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </button>
              ) : (
                <div style={{ fontSize: 11, color: '#6b7585', fontStyle: 'italic' }}>
                  Enter the billed amount in "Requested Reduction" to compute a dollar suggestion
                </div>
              )}
            </div>
          </div>
        )}

        <div style={styles.statsRow}>
          <div style={styles.stat}>
            <div style={styles.statLabel}>Median</div>
            <div style={styles.statValue}>{(median * 100).toFixed(0)}%</div>
            <div style={styles.statSub}>50th pct</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statLabel}>Common range</div>
            <div style={styles.statValue}>
              {(p25 * 100).toFixed(0)}–{(p75 * 100).toFixed(0)}%
            </div>
            <div style={styles.statSub}>25th–75th pct</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statLabel}>Sample size</div>
            <div style={styles.statValue}>{stats.n}</div>
            <div style={styles.statSub}>past cases</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    margin: '0 0 18px',
  },
  cardEmpty: {
    background: '#fafafa',
    border: '1px dashed #d1d5db',
    borderRadius: 8,
    padding: '14px 16px',
    margin: '0 0 18px',
    fontSize: 13,
  },
  header: {
    background: '#171D29',
    color: 'white',
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyebrow: {
    fontFamily: "'Arial Black', Impact, 'Helvetica Neue', sans-serif",
    fontSize: 14,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: '#A1B808',
    marginBottom: 4,
  },
  eyebrowEmpty: {
    fontFamily: "'Arial Black', Impact, 'Helvetica Neue', sans-serif",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: '#A1B808',
    marginBottom: 4,
  },
  subtitle: { fontSize: 12, color: '#d6dae0' },
  emptyMessage: { color: '#6b7585', fontSize: 13 },
  confidencePill: {
    fontSize: 10,
    fontWeight: 700,
    padding: '4px 10px',
    border: '1px solid',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.05)',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  body: { padding: 20 },
  suggestion: {
    textAlign: 'center',
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: '1px solid #e5e7eb',
  },
  suggestionLabel: {
    fontSize: 10,
    color: '#6b7585',
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 700,
    marginBottom: 6,
  },
  suggestionAmount: {
    fontFamily: "'Arial Black', Impact, 'Helvetica Neue', sans-serif",
    fontSize: 32,
    color: '#171D29',
    lineHeight: 1,
  },
  suggestionPct: {
    fontSize: 12,
    color: '#4F5A04',
    fontWeight: 600,
    marginTop: 4,
  },
  suggestionDetail: {
    fontSize: 11,
    color: '#6b7585',
    marginTop: 8,
  },
  ctaRow: { marginTop: 12 },
  primaryBtn: {
    background: '#A1B808',
    color: 'white',
    border: 'none',
    padding: '8px 18px',
    borderRadius: 8,
    fontFamily: "'Arial Black', Impact, 'Helvetica Neue', sans-serif",
    fontWeight: 900,
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontSize: 11,
    cursor: 'pointer',
  },
  matchedBadge: {
    color: '#16a34a',
    fontSize: 12,
    fontWeight: 600,
    padding: '8px 0',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  stat: {
    background: '#F3F3F3',
    padding: 10,
    borderRadius: 6,
    textAlign: 'center',
  },
  statLabel: {
    fontSize: 9,
    color: '#6b7585',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 700,
    marginBottom: 3,
  },
  statValue: {
    fontSize: 16,
    fontWeight: 700,
    color: '#171D29',
    lineHeight: 1.1,
  },
  statSub: {
    fontSize: 9,
    color: '#6b7585',
    marginTop: 2,
  },
};

export default MayLogicCard;
